/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { IconDownload } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ListView from "../../../components/common/ListView";
import PremiumSearch from "../../../components/common/PremiumSearch";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useLanguage } from "../../../context/LanguageContext";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { useShipmentInventoryListData } from "../../../hooks/inventoryHooks";
import formatDate from "../../../utilities/helper";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../../features/auth/authSlice";

interface InventoryTabProps {
  warehouseId?: string;
  searchQuery?: string;
  portalContainer?: HTMLDivElement | null;
  headerSubTabs?: React.ReactNode;
}

export default function InventoryTab({
  warehouseId: propWarehouseId,
  searchQuery,
  portalContainer,
  headerSubTabs,
}: InventoryTabProps = {}) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const profile = useSelector((state: any) => state.auth?.profile);
  const warehouseId = propWarehouseId || profile?.warehouse;

  const permissions = useSelector(selectClientPermissions);
  const { allowed: canExportShipmentInventory } = usePermission(
    "shipment_inventory",
    "export",
  );
  const showExport =
    !permissions || permissions.length === 0 || canExportShipmentInventory;

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [localSearch, setLocalSearch] = useState<string>("");

  const searchFor = searchQuery !== undefined ? searchQuery : localSearch;
  const setSearchFor = searchQuery !== undefined ? () => {} : setLocalSearch;
  const debouncedSearchFor = useDebounce(searchFor, 550);

  const { data, isLoading, refetch } = useShipmentInventoryListData(
    pageNo,
    limit,
    debouncedSearchFor,
    warehouseId,
  );

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor, warehouseId]);

  useEffect(() => {
    setPageNo(1);
  }, [warehouseId]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const handleExportXLSX = () => {
    const dataToExport = data?.inventoryMovement || [];
    if (dataToExport.length === 0) {
      toast.error(t("No data available to export"));
      return;
    }
    const exportData = dataToExport.map((row: any, idx: number) => ({
      [t("#")]: idx + 1,
      [t("AWB")]: row.awb || "",
      [t("Reference ID")]: row.referenceNumber || "",
      [t("Client")]: row.clientName || "",
      [t("Origin")]: row.origin || "",
      [t("Receiver City")]: row.receiverCity || "",
      [t("Receiver District")]: row.receiverDistrict || "",
      [t("Receiver Country")]: row.receiverCountry || "",
      [t("Location ID")]: row.locationName || "",
      [t("Created At")]: row.createdAt
        ? new Date(row.createdAt).toLocaleString()
        : "",
      [t("Update At")]: row.updatedAt
        ? new Date(row.updatedAt).toLocaleString()
        : "",
      [t("Update By")]: row.updatedBy || "",
      [t("Status")]: t("Shelved"),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("Shipment Inventory"));
    XLSX.writeFile(wb, "shipment_inventory_export.xlsx");
    toast.success(t("Exported successfully!"));
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "AWB", value: "awb", isShow: true },
    { text: "Reference ID", value: "referenceNumber", isShow: true },
    { text: "Client", value: "clientName", isShow: true },
    { text: "Origin", value: "origin", isShow: true },
    { text: "Destination", value: "destination", isShow: true },
    { text: "Warehouse", value: "warehouseName", isShow: true },
    { text: "ShelfId", value: "shelfId", isShow: true },
    { text: "Created At", value: "createdAt", isShow: true },
    { text: "Update At", value: "updateAt", isShow: true },
    { text: "Updated By", value: "updatedBy", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: t("#"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "awb":
          return {
            label: t("AWB"),
            render: (c: any) => (
              <button
                type="button"
                onClick={() => navigate(`/orders/${c.awb}`)}
                className="text-[#212121] hover:underline font-bold cursor-pointer"
              >
                {c.awb}
              </button>
            ),
          };
        case "referenceNumber":
          return {
            label: t("Reference"),
            render: (c: any) => c.referenceNumber,
          };
        case "clientName":
          return {
            label: t("Client"),
            render: (c: any) => c.clientName,
          };
        case "origin":
          return {
            label: t("Origin"),
            render: (c: any) => c.origin,
          };
        case "destination":
          return {
            label: t("Destination"),
            render: (c: any) => (
              <div className="flex flex-col items-center text-center">
                <div
                  className="text-xs font-bold text-[#212121] truncate"
                  title={`${c.receiverCity || ""}${c.receiverCountry ? `, ${c.receiverCountry}` : ""}`}
                >
                  {c.receiverCity || ""}
                  {c.receiverCountry ? `, ${c.receiverCountry}` : ""}
                </div>
                {c.receiverDistrict && (
                  <div className="text-[9px] text-slate-400 font-bold flex items-center gap-1 mt-1">
                    <span
                      className="truncate"
                      title={`${t("Dist")}: ${c.receiverDistrict}`}
                    >
                      {t("Dist")}: {c.receiverDistrict}
                    </span>
                  </div>
                )}
              </div>
            ),
          };
        case "warehouseName":
          return {
            label: t("Warehouse"),
            render: (c: any) => c.warehouseName,
          };
        case "shelfId":
          return {
            label: t("Location"),
            render: (c: any) => <p className="font-bold">{c.locationName}</p>,
          };
        case "createdAt":
          return {
            label: t("Created At"),
            render: (c: any) => formatDate(c.createdAt),
          };
        case "updateAt":
          return {
            label: t("Updated"),
            render: (c: any) => formatDate(c.updatedAt),
          };
        case "updatedBy":
          return {
            label: t("Updated By"),
            render: (c: any) => c.updatedBy,
          };
        case "status":
          return {
            label: t("Status"),
            render: () => (
              <div className="flex justify-center">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-center bg-slate-100 border border-slate-200 text-[#334155] text-xs font-medium cursor-default"
                >
                  {t("Shelved")}
                </button>
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const actionControls = (
    <>
      {searchQuery === undefined && (
        <PremiumSearch
          value={searchFor}
          placeholder={t("Search by AWB, client...")}
          onChange={(e) => {
            setSearchFor(e.target.value);
            setPageNo(1);
          }}
        />
      )}

      {showExport && (
        <button
          onClick={handleExportXLSX}
          className="flex items-center justify-center p-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 h-8 w-8 cursor-pointer"
          title={t("Export")}
        >
          <IconDownload size={17} stroke={2.5} />
        </button>
      )}

      {data && data.totalPages * limit > 10 && (
        <EntriesSelector
          value={limit}
          options={[
            10,
            ...(data.totalPages * limit > 10 ? [20] : []),
            ...(data.totalPages * limit > 20 ? [30] : []),
            ...(data.totalPages * limit > 50 ? [50] : []),
            ...(data.totalPages * limit > 100 ? [100] : []),
          ]}
          onChange={(val) => {
            setLimit(val);
            setPageNo(1);
          }}
        />
      )}

      <ColumnsFilter
        columnsData={columnsData.map((c) => ({ ...c, text: t(c.text) }))}
        setHeadings={setHeadings}
      />
    </>
  );

  return (
    <div>
      {portalContainer
        ? createPortal(actionControls, portalContainer)
        : !headerSubTabs && (
            <div className="flex justify-end items-center mb-3 gap-2.5">{actionControls}</div>
          )}

      <div className="rounded-r-3xl rounded-b-3xl border border-slate-100 shadow-sm overflow-visible">
        <div className="">
          {isLoading ? (
            <TableSkeleton
              columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
              rows={6}
            />
          ) : data?.inventoryMovement?.length ? (
            <ListView
              tdClassName="rounded-r-none rounded-l-none "
              mainDivClassName="bg-transparent "
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent "
              thClassName="border-t"
              data={data?.inventoryMovement}
              columns={columns}
            />
          ) : (
            <NoDataFound />
          )}
        </div>
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex justify-center items-center py-4">
          <Pagination
            currentPage={pageNo}
            totalPages={data.totalPages}
            fetchData={handlePaginationFetch}
          />
        </div>
      )}
    </div>
  );
}
